import React, { useState, useEffect } from 'react';
import '../../styles/design-tokens.css';

interface QuantityInputProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  showToast?: boolean;
  onBlur?: () => void;
  inputRef?: React.RefObject<HTMLInputElement | null>;
}

const QuantityInput: React.FC<QuantityInputProps> = ({
  value,
  onChange,
  min = 1,
  max = 9999,
  disabled = false,
  size = 'md',
  showToast = false,
  onBlur,
  inputRef
}) => {
  const [inputValue, setInputValue] = useState(value.toString());

  // Sync internal state with prop value
  useEffect(() => {
    setInputValue(value.toString());
  }, [value]);

  const handleIncrement = () => {
    const newValue = Math.min(value + 1, max);
    onChange(newValue);
    setInputValue(newValue.toString());
  };

  const handleDecrement = () => {
    const newValue = Math.max(value - 1, min);
    onChange(newValue);
    setInputValue(newValue.toString());
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;

    // Allow empty string for typing
    if (val === '') {
      setInputValue('');
      return;
    }

    // Only allow numbers
    if (!/^\d+$/.test(val)) {
      return;
    }

    setInputValue(val);
  };

  const handleInputBlur = () => {
    let numValue = parseInt(inputValue, 10);

    // Handle empty or invalid input - allow 0 if min is 0
    if (!inputValue || isNaN(numValue)) {
      numValue = min === 0 ? 0 : min;
    }

    // Enforce min/max bounds - but allow 0 if explicitly entered
    if (numValue === 0 && inputValue === '0') {
      // User explicitly wants 0, allow it
    } else {
      // Otherwise enforce min/max bounds
      numValue = Math.max(min, Math.min(numValue, max));
    }

    // Update both internal and parent state
    setInputValue(numValue.toString());
    if (numValue !== value) {
      onChange(numValue);
    }

    if (onBlur) {
      onBlur();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleInputBlur();
      (e.target as HTMLInputElement).blur();
    }
  };

  // Size-based styling - all buttons are 32px square as per design system
  const sizeStyles = {
    sm: {
      input: { width: '50px', height: '32px', fontSize: '14px' },
      button: { width: '32px', height: '32px', fontSize: '14px' }
    },
    md: {
      input: { width: '60px', height: '32px', fontSize: '16px' },
      button: { width: '32px', height: '32px', fontSize: '16px' }
    },
    lg: {
      input: { width: '70px', height: '32px', fontSize: '18px' },
      button: { width: '32px', height: '32px', fontSize: '18px' }
    }
  };

  const currentSize = sizeStyles[size];

  return (
    <div
      className="quantity-input-group"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        borderRadius: 'var(--radius-md)',
        background: 'var(--bg-secondary)',
        padding: '2px'
      }}
    >
      <button
        type="button"
        className="btn-quantity"
        onClick={handleDecrement}
        disabled={disabled || value <= min}
        style={{
          ...currentSize.button,
          border: 'none',
          background: disabled || value <= min
            ? 'var(--bg-secondary)'
            : 'white',
          color: disabled || value <= min
            ? 'var(--text-disabled)'
            : 'var(--text-primary)',
          borderRadius: 'var(--radius-sm)',
          cursor: disabled || value <= min ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all var(--transition-fast)',
          fontWeight: 'bold'
        }}
        aria-label="Decrease quantity"
      >
        <i className="fas fa-minus" style={{ fontSize: currentSize.button.fontSize }}></i>
      </button>

      <input
        ref={inputRef}
        type="text"
        className="quantity-input-field"
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onKeyPress={handleKeyPress}
        disabled={disabled}
        style={{
          ...currentSize.input,
          border: '1px solid var(--border-light)',
          borderRadius: 'var(--radius-sm)',
          textAlign: 'center',
          fontWeight: 'var(--font-weight-medium)',
          background: disabled ? 'var(--bg-secondary)' : 'white',
          color: disabled ? 'var(--text-disabled)' : 'var(--text-primary)',
          // Hide browser's default number arrows
          MozAppearance: 'textfield' as any,
          WebkitAppearance: 'none',
          appearance: 'none'
        }}
        inputMode="numeric"
        pattern="[0-9]*"
        aria-label="Quantity"
      />

      <button
        type="button"
        className="btn-quantity"
        onClick={handleIncrement}
        disabled={disabled || value >= max}
        style={{
          ...currentSize.button,
          border: 'none',
          background: disabled || value >= max
            ? 'var(--bg-secondary)'
            : 'white',
          color: disabled || value >= max
            ? 'var(--text-disabled)'
            : 'var(--text-primary)',
          borderRadius: 'var(--radius-sm)',
          cursor: disabled || value >= max ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all var(--transition-fast)',
          fontWeight: 'bold'
        }}
        aria-label="Increase quantity"
      >
        <i className="fas fa-plus" style={{ fontSize: currentSize.button.fontSize }}></i>
      </button>

      <style>{`
        /* Hide number input arrows for all browsers */
        .quantity-input-field::-webkit-outer-spin-button,
        .quantity-input-field::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }

        .quantity-input-field[type=number] {
          -moz-appearance: textfield;
        }

        .btn-quantity:hover:not(:disabled) {
          background: var(--color-primary-light) !important;
          transform: scale(1.05);
        }

        .btn-quantity:active:not(:disabled) {
          transform: scale(0.95);
        }

        .quantity-input-field:focus {
          outline: none;
          border-color: var(--color-primary) !important;
          box-shadow: 0 0 0 3px rgba(46, 80, 144, 0.1);
        }
      `}</style>
    </div>
  );
};

export default QuantityInput;